import React, { useState, useEffect } from 'react';
import AlumniTable from '../components/alumni/AlumniTable';
import CsvImport from '../components/alumni/CsvImport';
import { loadCsvFile } from '../utils/csvLoader';
import { batchSearchLinkedIn, getLastSyncTime, updateLastSyncTime, getAlumni, createAlumni, updateAlumni, deleteAlumni, importAlumni } from '../utils/apiService';
import { advancedSearch } from '../utils/searchUtils';
import { normalizeAlumniCountries, extractUniqueCountries } from '../utils/countryUtils';

const BATCH_SIZE = 50; // Process 50 records at a time

const AlumniList = () => {
  const [alumni, setAlumni] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batchSearching, setBatchSearching] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0, errors: 0 });
  const [lastSyncTime, setLastSyncTime] = useState('Never');
  const [batchActionStatus, setBatchActionStatus] = useState('');
  const [syncActionStatus, setSyncActionStatus] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [availableCountries, setAvailableCountries] = useState([]);

  // Process alumni data in batches
  const processBatch = async (data, startIndex) => {
    const endIndex = Math.min(startIndex + BATCH_SIZE, data.length);
    const batch = data.slice(startIndex, endIndex);
    
    try {
      await importAlumni(batch);
      return endIndex;
    } catch (err) {
      console.error(`Error processing batch ${startIndex}-${endIndex}:`, err);
      throw err;
    }
  };

  // Define loadAlumni function at component scope so it can be called from multiple places
  const loadAlumni = async (preserveCountryFilter = false) => {
    // Store current country filter if we need to preserve it
    const currentCountryFilter = preserveCountryFilter ? countryFilter : null;
    
    // Store current scroll position before loading data
    const scrollPosition = window.pageYOffset;
    
    // Don't show loading indicator for background refreshes
    if (!preserveCountryFilter) {
      setLoading(true);
    }
    setError(null);
    try {
      // Always clear localStorage to force reload from database
      localStorage.removeItem('alumni_data');
      
      // First try to fetch data directly from the database
      console.log('Fetching data from database first...');
      try {
        const dbAlumni = await getAlumni();
        
        if (dbAlumni && dbAlumni.length > 0) {
          console.log(`Fetched ${dbAlumni.length} records from database`);
          console.log('Sample record from DB:', dbAlumni[0]);
          
          // Normalize country names in alumni data
          const normalizedAlumni = normalizeAlumniCountries(dbAlumni);
          setAlumni(normalizedAlumni);
          
          // Extract unique normalized countries
          const countries = extractUniqueCountries(normalizedAlumni);
          setAvailableCountries(countries);
          
          // Restore country filter if needed
          if (preserveCountryFilter && currentCountryFilter) {
            // Only restore if the country still exists in the data
            if (currentCountryFilter === 'all' || countries.includes(currentCountryFilter)) {
              setCountryFilter(currentCountryFilter);
            }
          }
          
          // Save normalized data to localStorage for offline access
          localStorage.setItem('alumni_data', JSON.stringify(normalizedAlumni));
          setLoading(false);
          
          // Restore scroll position after a short delay to ensure rendering is complete
          if (preserveCountryFilter) {
            setTimeout(() => {
              window.scrollTo(0, scrollPosition);
            }, 10);
          }
          
          return; // Exit early if database fetch is successful
        }
        } catch (dbError) {
          console.error('Error fetching from database, falling back to CSV:', dbError);
        }
        
        // If database fetch fails or returns no data, load from CSV file
        console.log('Loading data from CSV file...');
        const data = await loadCsvFile('/data/former_employees.csv');
        
        if (data && data.length > 0) {
          console.log(`Loaded ${data.length} records from CSV file`);
          console.log('Sample record:', data[0]);
          
          // Process CSV data in batches and import to database
          let currentIndex = 0;
          while (currentIndex < data.length) {
            console.log(`Processing batch starting at index ${currentIndex}...`);
            currentIndex = await processBatch(data, currentIndex);
            console.log(`Processed up to index ${currentIndex}`);
          }
          
          // Fetch the updated data from the database after import
          console.log('Fetching updated data from database after import...');
          try {
            const updatedDbAlumni = await getAlumni();
            
            if (updatedDbAlumni && updatedDbAlumni.length > 0) {
              console.log(`Fetched ${updatedDbAlumni.length} records from database after import`);
              // Normalize country names in alumni data
              const normalizedAlumni = normalizeAlumniCountries(updatedDbAlumni);
              setAlumni(normalizedAlumni);
              
              // Extract unique normalized countries
              const countries = extractUniqueCountries(normalizedAlumni);
              setAvailableCountries(countries);
              
              localStorage.setItem('alumni_data', JSON.stringify(normalizedAlumni));
            } else {
              // If database fetch still fails, use the CSV data
              console.log('Using CSV data as fallback');
              // Normalize country names in CSV data
              const normalizedData = normalizeAlumniCountries(data);
              setAlumni(normalizedData);
              
              // Extract unique normalized countries
              const countries = extractUniqueCountries(normalizedData);
              setAvailableCountries(countries);
              
              // Restore country filter if needed
              if (preserveCountryFilter && currentCountryFilter) {
                // Only restore if the country still exists in the data
                if (currentCountryFilter === 'all' || countries.includes(currentCountryFilter)) {
                  setCountryFilter(currentCountryFilter);
                }
              }
              
              localStorage.setItem('alumni_data', JSON.stringify(normalizedData));
              
              // Restore scroll position if needed
              if (preserveCountryFilter) {
                setTimeout(() => {
                  window.scrollTo(0, scrollPosition);
                }, 10);
              }
            }
          } catch (finalDbError) {
            console.error('Error fetching from database after import, using CSV data:', finalDbError);
            // Normalize country names in CSV data
            const normalizedData = normalizeAlumniCountries(data);
            setAlumni(normalizedData);
            
            // Extract unique normalized countries
            const countries = extractUniqueCountries(normalizedData);
            setAvailableCountries(countries);
            
            // Restore country filter if needed
            if (preserveCountryFilter && currentCountryFilter) {
              // Only restore if the country still exists in the data
              if (currentCountryFilter === 'all' || countries.includes(currentCountryFilter)) {
                setCountryFilter(currentCountryFilter);
              }
            }
            
            localStorage.setItem('alumni_data', JSON.stringify(normalizedData));
            
            // Restore scroll position if needed
            if (preserveCountryFilter) {
              setTimeout(() => {
                window.scrollTo(0, scrollPosition);
              }, 10);
            }
          }
        } else {
          throw new Error('No data found in CSV file');
        }
      } catch (err) {
        console.error('Error loading alumni data:', err);
        setError(err.message || 'Failed to load alumni data');
      } finally {
        setLoading(false);
      }
    };

  // Load alumni data on component mount
  useEffect(() => {
    // Initial load without preserving filter (since there's no filter yet)
    loadAlumni(false);
  }, []); // Empty dependency array means this only runs once on mount
  
  // Set up a separate effect for the periodic refresh
  useEffect(() => {
    // This effect depends on countryFilter, so it will re-establish the interval
    // whenever the filter changes
    const refreshInterval = setInterval(() => {
      loadAlumni(true); // Always preserve the current filter
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(refreshInterval);
  }, [countryFilter]); // Re-establish interval when filter changes

  // Handle manual sync status check
  const handleSyncStatusCheck = async () => {
    try {
      const syncTime = await getLastSyncTime();
      setLastSyncTime(syncTime);
      setSyncActionStatus('Sync status updated');
      setTimeout(() => setSyncActionStatus(''), 3000);
    } catch (err) {
      setError('Failed to get sync status');
      setSyncActionStatus('Error checking sync status');
      setTimeout(() => setSyncActionStatus(''), 3000);
    }
  };

  // Handle CSV import
  const handleCsvImport = (data) => {
    setAlumni(data);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search type selection
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    // Update search term with prefix based on search type
    if (e.target.value !== 'all' && searchTerm.trim() !== '') {
      const prefixMap = {
        'name': 'name:',
        'company': 'company:',
        'position': 'position:',
        'email': 'email:',
        'phone': 'phone:',
        'linkedin': 'linkedin:'
      };
      // Only add prefix if it doesn't already have one
      const hasPrefix = Object.values(prefixMap).some(prefix => 
        searchTerm.toLowerCase().startsWith(prefix));
      
      if (!hasPrefix) {
        setSearchTerm(`${prefixMap[e.target.value]} ${searchTerm}`);
      }
    }
  };
  
  // Handle country filter change
  const handleCountryFilterChange = (e) => {
    setCountryFilter(e.target.value);
  };

  // Clear search and filters
  const clearSearch = () => {
    setSearchTerm('');
    setSearchType('all');
    setCountryFilter('all');
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  // Handle batch LinkedIn search
  const handleBatchSearch = async () => {
    if (batchSearching) return;
    
    setBatchSearching(true);
    setBatchProgress({ completed: 0, total: 100, errors: 0 });
    
    // Show searching label using direct DOM manipulation
    document.getElementById('batch-searching-label').style.opacity = '1';
    
    try {
      // First, ensure we have the latest data from localStorage
      const savedData = localStorage.getItem('alumni_data');
      let currentAlumni = alumni;
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData && parsedData.length > 0) {
            currentAlumni = parsedData;
            setAlumni(parsedData); // Update state with latest data
          }
        } catch (e) {
          console.error('Error parsing saved alumni data:', e);
        }
      }
      
      // Get the first 100 alumni without LinkedIn data
      const alumniToSearch = currentAlumni
        .filter(a => !a.linkedInUrl)
        .slice(0, 100);
      
      if (alumniToSearch.length === 0) {
        setError('No alumni without LinkedIn data found');
        setBatchSearching(false);
        
        // Update batch action status with direct DOM manipulation
        document.getElementById('batch-searching-label').style.opacity = '0';
        document.getElementById('batch-error-label').style.opacity = '1';
        setTimeout(() => {
          document.getElementById('batch-error-label').style.opacity = '0';
        }, 3000);
        
        return;
      }
      
      // Set the total for progress tracking
      setBatchProgress(prev => ({ ...prev, total: alumniToSearch.length }));
      
      // Call the batch search API
      await batchSearchLinkedIn(
        alumniToSearch,
        // Progress callback
        (progress) => {
          console.log(`Progress: ${progress.completed}/${progress.total}`);
          setBatchProgress(progress);
          
          // If we have a result, update the alumni data
          if (progress.result && progress.alumniId) {
            // Find the alumni item and update it
            const updatedAlumni = currentAlumni.map(item => {
              if (item.id === progress.alumniId) {
                const updatedItem = { ...item };
                
                // Update with LinkedIn data if found
                if (progress.result.linkedInUrl) {
                  updatedItem.linkedInUrl = progress.result.linkedInUrl;
                  updatedItem.currentTitle = progress.result.currentTitle || 'Not specified';
                  updatedItem.currentCompany = progress.result.currentCompany || 'Not specified';
                  updatedItem.reasoning = progress.result.reasoning || 'Found on LinkedIn';
                  updatedItem.lastUpdated = new Date().toISOString();
                } else {
                  // If no data was found, set default values
                  updatedItem.currentTitle = 'Not found';
                  updatedItem.currentCompany = 'Not found';
                  updatedItem.reasoning = progress.result.reasoning || 'Could not find information about this person';
                  updatedItem.lastUpdated = new Date().toISOString();
                }
                
                return updatedItem;
              }
              return item;
            });
            
            // Update both state and localStorage
            setAlumni(updatedAlumni);
            localStorage.setItem('alumni_data', JSON.stringify(updatedAlumni));
            currentAlumni = updatedAlumni; // Update our local reference
          }
        },
        // Complete callback
        (results) => {
          setBatchSearching(false);
          setLastSyncTime(updateLastSyncTime());
          console.log(`Batch search completed: ${results.length} alumni processed`);
          
          // Update batch action status with direct DOM manipulation
          document.getElementById('batch-searching-label').style.opacity = '0';
          document.getElementById('batch-complete-label').style.opacity = '1';
          setTimeout(() => {
            document.getElementById('batch-complete-label').style.opacity = '0';
          }, 3000);
          
          // Force a refresh of the data from localStorage
          const finalData = localStorage.getItem('alumni_data');
          if (finalData) {
            try {
              const parsedFinalData = JSON.parse(finalData);
              setAlumni(parsedFinalData);
            } catch (e) {
              console.error('Error parsing final alumni data:', e);
            }
          }
          
          // Trigger a window event to notify components to refresh
          window.dispatchEvent(new Event('alumni_data_updated'));
        }
      );
    } catch (err) {
      console.error('Error in batch search:', err);
      setError(`Batch search error: ${err.message}`);
      setBatchSearching(false);
      
      // Update batch action status with direct DOM manipulation
      document.getElementById('batch-searching-label').style.opacity = '0';
      document.getElementById('batch-error-label').style.opacity = '1';
      setTimeout(() => {
        document.getElementById('batch-error-label').style.opacity = '0';
      }, 3000);
    }
  };
  
  // Refresh the last sync time
  const refreshLastSyncTime = () => {
    const newSyncTime = updateLastSyncTime();
    setLastSyncTime(newSyncTime);
  };

  // Handle creating a new alumnus
  const handleCreateAlumni = async (newAlumni) => {
    setLoading(true);
    setError(null);
    try {
      const data = await createAlumni(newAlumni);
      setAlumni(prev => [...prev, data]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting an alumnus
  const handleDeleteAlumni = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await deleteAlumni(id);
      setAlumni(prev => prev.filter(alumnus => alumnus._id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle editing an alumnus
  const handleEditAlumni = async (updatedAlumni) => {
    setLoading(true);
    setError(null);
    try {
      const data = await updateAlumni(updatedAlumni._id, updatedAlumni);
      setAlumni(prev => prev.map(a => a._id === data._id ? data : a));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-6 bg-white rounded-xl shadow-md overflow-hidden">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
          <h1 className="text-2xl font-bold text-white">Alumni List</h1>
        </div>
        
        {/* Search and actions container */}
        <div className="p-6">
          {/* Search bar and import button */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div className="w-full lg:w-3/4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex flex-col space-y-3">
                  {/* Search input with dropdown */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="relative flex-grow">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder={searchType !== 'all' ? `Search by ${searchType}...` : "Search alumni..."}
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                      {searchTerm && (
                        <button
                          onClick={clearSearch}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                          aria-label="Clear search"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <select
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      className="py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 shadow-sm transition-all duration-200"
                      aria-label="Search field type"
                    >
                      <option value="all">All Fields</option>
                      <option value="name">Name</option>
                      <option value="company">Company</option>
                      <option value="position">Position/Title</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="linkedin">LinkedIn</option>
                    </select>
                  </div>
                  
                  {/* Country filter */}
                  <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg border border-blue-100">
                    <div className="font-medium text-blue-700">Filter by Country:</div>
                    <select
                      value={countryFilter}
                      onChange={handleCountryFilterChange}
                      className="py-2 px-4 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 shadow-sm transition-all duration-200 flex-grow"
                      aria-label="Filter by country"
                    >
                      <option value="all">All Countries</option>
                      {availableCountries.map((country, index) => (
                        <option key={index} value={country}>{country}</option>
                      ))}
                    </select>
                    {countryFilter !== 'all' && (
                      <button
                        onClick={() => setCountryFilter('all')}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition-colors duration-200"
                        aria-label="Clear country filter"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* Search tips */}
                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded-md border border-blue-100">
                    <span className="font-medium text-blue-700">Pro tip:</span> Use prefixes like <code className="bg-white px-1.5 py-0.5 rounded border border-blue-100 text-blue-700">name:</code>, <code className="bg-white px-1.5 py-0.5 rounded border border-blue-100 text-blue-700">company:</code>, or <code className="bg-white px-1.5 py-0.5 rounded border border-blue-100 text-blue-700">position:</code> for targeted searches.
                  </div>
                </div>
              </div>
            </div>
            
            {/* Import CSV button */}
            <div className="w-full sm:w-auto">
              <CsvImport onImport={handleCsvImport} />
            </div>
          </div>
          
          {/* Action buttons row */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
            {/* Action Status Display for Batch Search - Fixed labels */}
            <div className="flex flex-col items-start">
              <div className="relative w-full">
                <div id="batch-search-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Search 100 People on LinkedIn
                </div>
                <div id="batch-searching-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Searching LinkedIn...
                </div>
                <div id="batch-complete-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Batch search completed!
                </div>
                <div id="batch-error-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Batch search failed
                </div>
                <div className="h-4"></div> {/* Spacer */}
              </div>
              <button
                onClick={() => {
                  // Display action name using direct DOM manipulation
                  document.getElementById('batch-search-label').style.opacity = '0';
                  document.getElementById('batch-searching-label').style.opacity = '1';
                  
                  // Call the batch search function
                  handleBatchSearch();
                }}
                disabled={batchSearching}
                className={`flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium shadow-md transition-all duration-200 ${batchSearching ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'}`}
                onMouseEnter={() => {
                  if (!batchSearching) {
                    document.getElementById('batch-search-label').style.opacity = '1';
                  }
                }}
                onMouseLeave={() => {
                  if (!batchSearching) {
                    document.getElementById('batch-search-label').style.opacity = '0';
                  }
                }}
              >
                {batchSearching ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Searching LinkedIn ({batchProgress.completed}/{batchProgress.total})</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    <span>Search 100 People on LinkedIn</span>
                  </>
                )}
              </button>
            </div>
          
            <div className="flex flex-col items-start">
              <div className="relative w-full">
                <div id="sync-refresh-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Refresh sync time
                </div>
                <div id="sync-refreshing-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Refreshing sync time...
                </div>
                <div id="sync-updated-label" className="absolute top-0 left-0 right-0 text-xs text-blue-600 font-medium mb-1 h-4 opacity-0 transition-opacity duration-150">
                  Sync time updated
                </div>
                <div className="h-4"></div> {/* Spacer */}
              </div>
              <div className="flex items-center bg-blue-50 px-5 py-3 rounded-lg border border-blue-100 shadow-sm">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span className="text-gray-700 mr-2 font-medium">Last Sync:</span>
                  <span className="font-bold text-blue-700">{lastSyncTime}</span>
                </div>
                <button 
                  onClick={() => {
                    // Show refreshing label
                    document.getElementById('sync-refresh-label').style.opacity = '0';
                    document.getElementById('sync-refreshing-label').style.opacity = '1';
                    
                    refreshLastSyncTime();
                    
                    setTimeout(() => {
                      document.getElementById('sync-refreshing-label').style.opacity = '0';
                      document.getElementById('sync-updated-label').style.opacity = '1';
                      
                      setTimeout(() => {
                        document.getElementById('sync-updated-label').style.opacity = '0';
                      }, 2000);
                    }, 500);
                  }}
                  className="ml-3 text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-100 rounded-full transition-colors duration-200"
                  title="Refresh last sync time"
                  aria-label="Refresh last sync time"
                  onMouseEnter={() => {
                    document.getElementById('sync-refresh-label').style.opacity = '1';
                  }}
                  onMouseLeave={() => {
                    document.getElementById('sync-refresh-label').style.opacity = '0';
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="flex justify-center">
            <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-lg text-gray-600 mt-4">Loading alumni data...</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <p className="text-lg text-red-600 mb-4">{error}</p>
          <p className="text-gray-500">Please try importing a CSV file manually.</p>
          <button 
            onClick={() => setError(null)} 
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md"
          >
            Dismiss
          </button>
        </div>
      ) : alumni.length > 0 ? (
        <>
          {countryFilter !== 'all' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"></path>
                  </svg>
                  <span className="font-medium text-green-800">Filtering by country: <span className="font-bold">{countryFilter}</span></span>
                </div>
                <button 
                  onClick={() => setCountryFilter('all')}
                  className="text-green-600 hover:text-green-800 p-1 hover:bg-green-100 rounded-full transition-colors duration-200"
                  aria-label="Clear country filter"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
          )}
          <AlumniTable 
            alumni={countryFilter === 'all' ? alumni : alumni.filter(alumnus => alumnus.emp_country === countryFilter)} 
            searchTerm={searchTerm} 
            sortConfig={sortConfig} 
            requestSort={requestSort} 
          />
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="text-blue-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <p className="text-lg text-gray-600 mb-4">No alumni data available.</p>
          <p className="text-gray-500 mb-6">Import a CSV file to view alumni data.</p>
        </div>
      )}
    </div>
  );
};

export default AlumniList;
